import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, TextField, Typography } from "@mui/material";

const TIMES = ["15:00", "15:30", "16:00", "16:30", "17:00", "17:30"];

function madridNow() {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Madrid",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const get = (type) => parts.find((part) => part.type === type).value;
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    minutes: Number(get("hour")) * 60 + Number(get("minute")),
  };
}

function addDays(iso, days) {
  const [year, month, day] = iso.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function weekday(iso) {
  const [year, month, day] = iso.split("-").map(Number);
  const js = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return js === 0 ? 7 : js;
}

function toMinutes(time) {
  const [hour, minute] = time.split(":").map(Number);
  return hour * 60 + minute;
}

function buildDays() {
  const now = madridNow();
  const days = [];
  for (let offset = 0; offset < 28; offset += 1) {
    const date = addDays(now.date, offset);
    if (weekday(date) > 5) continue;
    const slots = TIMES.filter((time) => date > now.date || toMinutes(time) > now.minutes);
    if (slots.length) days.push({ date, slots });
  }
  return days;
}

const AcademyBooking = ({ topic = "" }) => {
  const { t } = useTranslation();
  const days = useMemo(() => buildDays(), []);
  const [booked, setBooked] = useState(() => new Set());
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch("/academy-book.php")
      .then((response) => response.json())
      .then((data) => {
        if (!cancelled && data && Array.isArray(data.booked)) {
          setBooked(new Set(data.booked));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (!selected) {
      setStatus("error");
      setMessage(t("academy.bookPickSlot"));
      return;
    }
    setSending(true);
    setStatus("");
    try {
      const response = await fetch("/academy-book.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          phone,
          topic,
          date: selected.date,
          time: selected.time,
          website,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.error || "fail");
      }
      setBooked((prev) => new Set(prev).add(`${selected.date}T${selected.time}`));
      setStatus("ok");
      setMessage(t("academy.bookOk", { date: data.date, time: data.time }));
      setSelected(null);
      setName("");
      setEmail("");
      setPhone("");
    } catch (error) {
      setStatus("error");
      setMessage(t("academy.bookFail"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Box id="reservar" component="section" sx={{ maxWidth: 860, mx: "auto", px: 2, py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: "#111", mb: 1 }}>
        {t("academy.bookTitle")}
      </Typography>
      <Typography sx={{ color: "#111", mb: 2 }}>{t("academy.bookSubtitle")}</Typography>
      {topic ? (
        <Typography sx={{ mb: 2, fontWeight: 600 }}>{topic}</Typography>
      ) : null}
      <Box sx={{ display: "grid", gap: 2 }}>
        {days.map((day) => (
          <Box key={day.date}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>{day.date}</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {day.slots.map((time) => {
                const id = `${day.date}T${time}`;
                const taken = booked.has(id);
                const active = selected && selected.date === day.date && selected.time === time;
                return (
                  <Button
                    key={id}
                    disabled={taken}
                    variant={active ? "contained" : "outlined"}
                    onClick={() => setSelected({ date: day.date, time })}
                    sx={{
                      color: active ? "#fff" : "#111",
                      borderColor: "#111",
                      backgroundColor: active ? "#111" : "#fff",
                      textTransform: "none",
                    }}
                  >
                    {time}
                  </Button>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
      <Box component="form" onSubmit={submit} sx={{ mt: 3, display: "grid", gap: 1.5, maxWidth: 420 }}>
        <TextField label={t("academy.bookName")} value={name} onChange={(event) => setName(event.target.value)} required />
        <TextField label={t("academy.bookEmail")} type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
        <TextField label={t("academy.bookPhone")} value={phone} onChange={(event) => setPhone(event.target.value)} />
        <input
          name="website"
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
          autoComplete="off"
          tabIndex={-1}
          aria-hidden="true"
          style={{ position: "absolute", left: "-9999px" }}
        />
        <Button
          type="submit"
          disabled={sending}
          variant="contained"
          sx={{ backgroundColor: "#111", textTransform: "none", fontWeight: 700, py: 1.2 }}
        >
          {t("academy.bookCta")}
        </Button>
        {message ? (
          <Typography sx={{ color: status === "ok" ? "#0b6b2f" : "#8a1c1c" }}>{message}</Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default AcademyBooking;
